import React from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getTextbookById, deleteTextbook } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate, useParams } from "react-router-dom";
import {
  IndividualViewPageLayout,
  SidebarSection,
  ActionButtons,
  DetailField,
  ActionButton,
  ActionButtonDanger,
} from "../../components/layout";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  Edit,
  Trash2,
  DollarSign,
  Tag,
  Phone,
  Calendar,
  User,
  BookOpen,
  GraduationCap,
} from "lucide-react";

export default function ViewTextbookPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();

  const {
    data: item,
    isLoading,
    error,
  } = useQuery(getTextbookById, { itemId: itemId || "" });
  const deleteTextbookFn = useAction(deleteTextbook);

  const handleEdit = () => {
    if (selectedLocation?.locationCode) {
      navigate(
        `/locations/${selectedLocation.locationCode}/textbooks/${itemId}/edit`,
      );
    } else {
      navigate(`/textbooks/${itemId}/edit`);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this listing?")) {
      try {
        await deleteTextbookFn({ itemId: itemId || "" });
        if (selectedLocation?.locationCode) {
          navigate(`/locations/${selectedLocation.locationCode}/textbooks`);
        } else {
          navigate("/textbooks");
        }
      } catch (error) {
        console.error("Error deleting item:", error);
        alert("Error deleting item. Please try again.");
      }
    }
  };

  if (!user)
    return (
      <DashboardLayout>
        <div>Authentication Required</div>
      </DashboardLayout>
    );
  if (!selectedLocation)
    return (
      <DashboardLayout>
        <div>No Location Selected</div>
      </DashboardLayout>
    );
  if (isLoading)
    return (
      <DashboardLayout>
        <div>Loading...</div>
      </DashboardLayout>
    );
  if (error || !item)
    return (
      <DashboardLayout>
        <div>Item Not Found</div>
      </DashboardLayout>
    );

  const getCategoryColor = (category: string) => {
    return category === "BUY"
      ? "bg-success/10 text-success"
      : category === "BORROW"
        ? "bg-warning/10 text-warning"
        : "bg-primary/10 text-primary";
  };

  const isAdmin = user?.isAdmin;
  const currentPageUrl =
    typeof window !== "undefined" ? window.location.href : "";

  const breadcrumbItems = [
    {
      label: "Academic Resources",
      dropdownItems: [
        {
          label: "Textbooks",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/textbooks`
            : "/textbooks",
        },
      ],
    },
    {
      label: "Textbooks",
      isClickable: true,
      href: selectedLocation?.locationCode
        ? `/locations/${selectedLocation.locationCode}/textbooks`
        : "/textbooks",
    },
  ];

  const sidebarContent = (
    <>
      <SidebarSection title="Actions">
        <ActionButtons>
          {isAdmin ? (
            <>
              <ActionButton onClick={handleEdit} icon={Edit}>
                Edit Listing
              </ActionButton>
              <ActionButtonDanger onClick={handleDelete} icon={Trash2}>
                Delete Listing
              </ActionButtonDanger>
            </>
          ) : (
            <div className="text-sm text-gray-500 text-center">
              Contact the seller to arrange a meet-up.
            </div>
          )}
        </ActionButtons>
      </SidebarSection>
      <SidebarSection title="Details">
        <div className="space-y-4">
          <DetailField
            label="Type"
            value={(item as any).metadata?.category || ""}
          />
          <DetailField
            label="Condition"
            value={(item as any).metadata?.condition?.replace("_", " ") || ""}
          />
          <DetailField
            label="Posted"
            value={new Date((item as any).createdAt).toLocaleDateString()}
          />
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={breadcrumbItems}
      currentPageUrl={currentPageUrl}
      sidebarContent={sidebarContent}
    >
      <div className="flex items-center gap-3 mb-4">
        <span
          className={`px-3 py-1 text-sm font-medium rounded ${getCategoryColor(
            (item as any).metadata?.category,
          )}`}
        >
          {(item as any).metadata?.category}
        </span>
        {(item as any).metadata?.condition && (
          <span className="px-3 py-1 text-sm font-medium rounded bg-gray-100 text-gray-800">
            {(item as any).metadata.condition?.replace("_", " ")}
          </span>
        )}
      </div>

      <h1 className="text-3xl font-bold text-gray-900 mb-2">
        {(item as any).title}
      </h1>

      <div className="flex items-center gap-2 text-xl text-gray-700 mb-6">
        <span>by {(item as any).metadata?.author}</span>
      </div>

      {(item as any).metadata?.price && (
        <div className="text-3xl font-bold text-green-600 mb-6">
          ${(item as any).metadata.price}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5" /> Book Details
          </h3>
          <dl className="space-y-3">
            {(item as any).metadata?.isbn && (
              <div>
                <dt className="text-sm font-medium text-gray-500">ISBN</dt>
                <dd className="text-gray-900">{(item as any).metadata.isbn}</dd>
              </div>
            )}
            {(item as any).metadata?.course && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Course</dt>
                <dd className="text-gray-900 bg-blue-100 text-blue-800 px-2 py-0.5 rounded inline-block">
                  {(item as any).metadata.course}
                </dd>
              </div>
            )}
          </dl>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5" /> Contact
          </h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Posted By</dt>
              <dd className="text-gray-900">
                {(item as any).author?.name || (item as any).author?.email}
              </dd>
            </div>
            {(item as any).metadata?.contactInfo && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Contact Info
                </dt>
                <dd className="text-gray-900">
                  {(item as any).metadata.contactInfo}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Description
        </h2>
        <div className="prose max-w-none text-gray-700 whitespace-pre-wrap">
          {(item as any).description}
        </div>
      </div>
    </IndividualViewPageLayout>
  );
}
